import { z } from 'zod'

import { ATA_FOOTER } from './ata-model'
import { DUE_SOON_DAYS } from './due-state'
import { REGULAR_BAND } from './performance'
import { RAS_FOOTER, RAS_OBJETIVO } from './ras-model'

/**
 * Configurações do gestor (`supply.setting`, chave → valor). Chave sem linha
 * no banco = padrão daqui. Espelho de `supply.setting_is_valid` na migration
 * 20261022210000_configuracoes.sql — mudou um, mude o outro e o teste SQL 012.
 */

const days = z.number().int().min(0).max(30)
const text = (max: number) => z.string().max(max)
const filled = (max: number) => z.string().max(max).refine((s) => s.trim() !== '', 'Não pode ficar em branco')

export const SETTING_SCHEMAS = {
  /** Faixa "Regular": de (meta − faixa) até a meta. */
  'desempenho.faixa_regular': z.number().int().min(0).max(100),
  /** Faltando até N dias, o prazo fica em destaque na tela. */
  'prazo.dias_em_breve': days,
  /** Aviso no sino N dias antes do prazo da tarefa (0 = não avisa antes). */
  'aviso.prazo_dias_antes': days,
  'aviso.prazo_no_dia': z.boolean(),
  /** Aviso no primeiro dia de atraso. */
  'aviso.atraso': z.boolean(),
  /** Aviso da decisão Q3 N dias antes do prazo (0 = não avisa). */
  'aviso.decisao_dias_antes': days,
  'ata.empresa': filled(80),
  'ata.q3_rodape': text(300),
  'ata.ras_objetivo': filled(1000),
  'ata.ras_rodape': text(300),
  'ata.q3_codigo': text(40),
  'ata.ras_codigo': text(40),
  'ata.revisao': text(20),
  // Perfil do sistema (migration 20261022220000_perfil_do_sistema.sql).
  /** Nome do sistema: 1ª linha da sidebar ("Central de Tarefas"). */
  'sistema.nome': filled(60),
  /** Nome do time: completa o nome ("Central de Tarefas Supply"). */
  'sistema.time': filled(40),
  /** Linha pequena da sidebar ("Supply Chain"); em branco, mostra o time. */
  'sistema.subtitulo': text(40),
  /** Gestor dono (id de `supply.member`); vazio = sem dono definido. */
  'sistema.gestor_id': z.union([z.literal(''), z.string().uuid()]),
} as const

export type SettingKey = keyof typeof SETTING_SCHEMAS
export type Settings = { readonly [K in SettingKey]: z.infer<(typeof SETTING_SCHEMAS)[K]> }

export const SETTING_DEFAULTS: Settings = {
  'desempenho.faixa_regular': REGULAR_BAND,
  'prazo.dias_em_breve': DUE_SOON_DAYS,
  'aviso.prazo_dias_antes': 1,
  'aviso.prazo_no_dia': true,
  'aviso.atraso': true,
  'aviso.decisao_dias_antes': 1,
  'ata.empresa': 'FLEXIBASE',
  'ata.q3_rodape': ATA_FOOTER,
  'ata.ras_objetivo': RAS_OBJETIVO,
  'ata.ras_rodape': RAS_FOOTER,
  'ata.q3_codigo': 'R01',
  'ata.ras_codigo': '',
  'ata.revisao': '',
  'sistema.nome': 'Central de Tarefas',
  'sistema.time': 'Supply',
  'sistema.subtitulo': 'Supply Chain',
  'sistema.gestor_id': '',
}

/** Nome completo do sistema: "Central de Tarefas Supply". */
export function systemName(settings: Settings): string {
  return `${settings['sistema.nome'].trim()} ${settings['sistema.time'].trim()}`.trim()
}

/** Linha pequena da sidebar: o subtítulo ou, em branco, o time. */
export function systemSubtitle(settings: Settings): string {
  return settings['sistema.subtitulo'].trim() || settings['sistema.time'].trim()
}

export const SETTING_KEYS = Object.keys(SETTING_SCHEMAS) as SettingKey[]

export interface SettingRow {
  readonly key: string
  readonly value: unknown
  readonly updated_at?: string
  readonly updated_by?: string | null
}

/** Linhas do banco → configurações completas. Valor inválido cai no padrão. */
export function resolveSettings(rows: readonly SettingRow[]): Settings {
  const out: Record<string, unknown> = { ...SETTING_DEFAULTS }
  for (const row of rows) {
    if (!(row.key in SETTING_SCHEMAS)) continue
    const parsed = SETTING_SCHEMAS[row.key as SettingKey].safeParse(row.value)
    if (parsed.success) out[row.key] = parsed.data
  }
  return out as Settings
}

/** "R01 · Rev. 02" — vazio quando não há código nem revisão. */
export function ataDocCode(settings: Settings, tipo: 'q3' | 'ras'): string {
  const code = settings[tipo === 'q3' ? 'ata.q3_codigo' : 'ata.ras_codigo'].trim()
  const rev = settings['ata.revisao'].trim()
  return [code, rev && `Rev. ${rev}`].filter(Boolean).join(' · ')
}

/** Textos das ATAs que vêm das configurações (entram no snapshot ao fechar). */
export interface AtaTexts {
  readonly company: string
  readonly footer: string
  readonly docCode: string
  /** Nome do sistema, no rodapé de cada página do PDF. */
  readonly system: string
}

export function ataTexts(settings: Settings, tipo: 'q3' | 'ras'): AtaTexts & { readonly objetivo: string } {
  return {
    system: systemName(settings),
    company: settings['ata.empresa'].trim(),
    footer: settings[tipo === 'q3' ? 'ata.q3_rodape' : 'ata.ras_rodape'].trim(),
    docCode: ataDocCode(settings, tipo),
    objetivo: settings['ata.ras_objetivo'].trim(),
  }
}
